import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';

const METRIC_TYPES = [
  { value: 'count', label: 'Count (e.g., 47 tows)' },
  { value: 'percentage', label: 'Percentage (e.g., 95.2%)' },
  { value: 'currency', label: 'Currency (e.g., $1,250)' },
  { value: 'duration', label: 'Duration (e.g., 23 min)' },
  { value: 'score', label: 'Score (e.g., 8.5)' },
];

const PERIOD_TYPES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const INPUT_METHODS = [
  { value: 'manual', label: 'Manual Entry' },
  { value: 'csv', label: 'CSV Upload' },
  { value: 'system', label: 'System-Calculated' },
  { value: 'api', label: 'API Integration' },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const SYSTEM_QUERY_TYPES = [
  { value: 'avg_response_time', label: 'Average Response Time (per driver)' },
  { value: 'total_calls_resolved', label: 'Total Calls Resolved (per driver)' },
  { value: 'completion_rate', label: 'Completion Rate % (per driver)' },
];

const SCORING_MODES = [
  { value: 'goal', label: '🎯 Goal-Based', desc: 'Score = actual ÷ goal × 100. Can exceed 100%.' },
  { value: 'relative_high', label: '🏆 Relative (Highest Wins)', desc: 'Top performer = 100, others proportional.' },
  { value: 'relative_low', label: '🛡️ Relative (Lowest Wins)', desc: 'Lowest value = 100, others proportional. For losses/penalties.' },
];

const EMPTY_FORM = {
  name: '', description: '', metric_type: 'count', value_direction: 'higher_is_better',
  round_mode: 'manual', period_type: 'weekly', input_method: 'manual',
  start_day: 'Monday', start_time: '08:00', timezone: 'America/Chicago',
  system_query_type: 'avg_response_time',
  display_format: '', goal: '', cumulative_label: 'Cumulative',
  value_sign: 'positive',
  include_in_composite: true,
  aggregation_mode: 'sum',
  scoring_mode: 'goal',
};

export default function KpiSetupPage() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getLeaderboardKpis();
      setKpis(Array.isArray(data) ? data : []);
    } catch { setKpis([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  }

  function openEdit(kpi) {
    let config = {};
    try { config = typeof kpi.period_config === 'string' ? JSON.parse(kpi.period_config) : (kpi.period_config || {}); } catch { /* */ }
    let sysConfig = {};
    try { sysConfig = kpi.system_query_config ? (typeof kpi.system_query_config === 'string' ? JSON.parse(kpi.system_query_config) : kpi.system_query_config) : {}; } catch { /* */ }

    setEditing(kpi);
    setForm({
      name: kpi.name || '',
      description: kpi.description || '',
      metric_type: kpi.metric_type || 'count',
      value_direction: kpi.value_direction || 'higher_is_better',
      round_mode: kpi.round_mode || 'manual',
      period_type: kpi.period_type || 'weekly',
      input_method: kpi.input_method || 'manual',
      start_day: config.start_day || 'Monday',
      start_time: config.start_time || '08:00',
      timezone: config.timezone || 'America/Chicago',
      system_query_type: sysConfig.query_type || 'avg_response_time',
      display_format: kpi.display_format || '',
      goal: kpi.goal ?? '',
      cumulative_label: kpi.cumulative_label || 'Cumulative',
      value_sign: kpi.value_sign || 'positive',
      include_in_composite: kpi.include_in_composite === 0 || kpi.include_in_composite === false ? false : true,
      aggregation_mode: kpi.aggregation_mode || 'sum',
      scoring_mode: kpi.scoring_mode || 'goal',
    });
    setError('');
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        metric_type: form.metric_type,
        value_direction: form.value_direction,
        round_mode: form.round_mode,
        period_type: form.period_type,
        period_config: {
          start_day: form.start_day,
          start_time: form.start_time,
          timezone: form.timezone,
        },
        input_method: form.input_method,
        system_query_config: form.input_method === 'system' ? { query_type: form.system_query_type } : null,
        display_format: form.display_format || null,
        goal: form.goal,
        cumulative_label: form.cumulative_label,
        value_sign: form.value_sign || 'positive',
        include_in_composite: form.include_in_composite !== false,
        aggregation_mode: form.aggregation_mode === 'latest' ? 'latest' : 'sum',
        scoring_mode: form.scoring_mode || 'goal',
      };

      if (editing) {
        await api.updateLeaderboardKpi(editing.id, payload);
        setSuccess('KPI updated');
      } else {
        await api.createLeaderboardKpi(payload);
        setSuccess('KPI created');
      }
      setShowModal(false);
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    try {
      await api.deleteLeaderboardKpi(id);
      setDeleteConfirm(null);
      load();
      setSuccess('KPI deactivated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); setDeleteConfirm(null); }
  }

  async function handleReset(id) {
    try {
      await api.resetLeaderboardKpi(id);
      setResetConfirm(null);
      setSuccess('KPI reset — all rounds and data cleared');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); setResetConfirm(null); }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  const periodBadgeColors = { weekly: '#3b82f6', monthly: '#8b5cf6', quarterly: '#f59e0b', yearly: '#10b981' };
  const inputBadgeColors = { manual: '#6b7280', csv: '#0891b2', system: '#10b981', api: '#d97706' };
  const modeBadgeColors = { manual: '#3b82f6', auto: '#10b981' };

  return (
    <div className="admin-page">
      <div className="maint-types__header kpi-setup-header">
        <h1>KPI Setup</h1>
        <div className="kpi-setup-header__actions" style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-gold" onClick={openAdd}>+ Create New KPI</button>
          {isSuperAdmin && (
            <button
              className="btn btn-outline"
              onClick={() => navigate('/admin-mod/leaderboard-integrations')}
              title="Configure API integrations that feed KPIs automatically"
            >
              🔌 Program New API
            </button>
          )}
        </div>
      </div>

      <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>
        Define KPIs and their measurement settings. Assign them to groups on the Groups page. Click <b>Program New API</b> to connect an external data source (SPOTIO, Google Sheets, etc.) that feeds a KPI automatically.
      </p>

      {error && !showModal && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? <div className="loading-spinner">Loading...</div> : kpis.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No KPIs configured yet. Create one to get started.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {kpis.map(kpi => (
            <div key={kpi.id} style={{
              background: '#fff', borderRadius: '12px', padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>{kpi.name}</h3>
                <div className="kpi-card-badges" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: `${periodBadgeColors[kpi.period_type] || '#6b7280'}15`, color: periodBadgeColors[kpi.period_type] || '#6b7280' }}>
                    {kpi.period_type}
                  </span>
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: `${modeBadgeColors[kpi.round_mode || 'manual']}15`, color: modeBadgeColors[kpi.round_mode || 'manual'] }}>
                    {kpi.round_mode === 'auto' ? 'auto' : 'manual'}
                  </span>
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: `${inputBadgeColors[kpi.input_method]}15`, color: inputBadgeColors[kpi.input_method] }}>
                    {kpi.input_method}
                  </span>
                  {kpi.scoring_mode && kpi.scoring_mode !== 'goal' && (
                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: kpi.scoring_mode === 'relative_low' ? '#fef2f215' : '#6d28d915', color: kpi.scoring_mode === 'relative_low' ? '#b91c1c' : '#6d28d9' }}>
                      {kpi.scoring_mode === 'relative_high' ? '🏆 relative' : '🛡️ low wins'}
                    </span>
                  )}
                  {kpi.value_sign === 'negative' && (
                    <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '4px', background: '#fef2f2', color: '#b91c1c' }}>
                      ➖ NEGATIVE
                    </span>
                  )}
                  {(kpi.include_in_composite === 0 || kpi.include_in_composite === false) && (
                    <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '4px', background: '#fef3c7', color: '#92400e' }}>
                      DISPLAY ONLY
                    </span>
                  )}
                </div>
              </div>
              {kpi.description && <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#64748b' }}>{kpi.description}</p>}
              <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>
                {METRIC_TYPES.find(m => m.value === kpi.metric_type)?.label?.split('(')[0]} &middot; {kpi.value_direction === 'higher_is_better' ? 'Higher is better' : 'Lower is better'}
              </div>
              {kpi.goal && (
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  Goal: <strong>{kpi.goal.toLocaleString()}</strong>
                  {kpi.scoring_mode === 'goal' && <span style={{ color: '#94a3b8' }}> (score = actual ÷ goal)</span>}
                </div>
              )}
              {kpi.cumulative_label && kpi.cumulative_label !== 'Cumulative' && (
                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
                  Totals label: <strong style={{ color: '#64748b' }}>{kpi.cumulative_label}</strong>
                </div>
              )}
              {kpi.round_mode === 'auto' && (
                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>
                  Auto-rounds: {kpi.period_type}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button className="btn btn-sm btn-outline" onClick={() => openEdit(kpi)}>Edit</button>
                {isSuperAdmin && <button className="btn btn-sm btn-outline" style={{ color: '#f59e0b' }} onClick={() => setResetConfirm(kpi)}>Reset</button>}
                <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(kpi)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <Modal title={editing ? 'Edit KPI' : 'New KPI'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave}>
            {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

            <div className="form-group">
              <label>KPI Name *</label>
              <input name="name" value={form.name} onChange={handleChange} className="form-input" placeholder="e.g., Weekly Tow Count" />
            </div>

            <div className="form-group">
              <label>Description</label>
              <input name="description" value={form.description} onChange={handleChange} className="form-input" placeholder="What does this KPI measure?" />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Metric Type</label>
                <select name="metric_type" value={form.metric_type} onChange={handleChange} className="form-input">
                  {METRIC_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Direction</label>
                <select name="value_direction" value={form.value_direction} onChange={handleChange} className="form-input">
                  <option value="higher_is_better">Higher is better</option>
                  <option value="lower_is_better">Lower is better</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>KPI Sign <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>— negative KPIs drag down the overall ranking</span></label>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <label style={{
                  flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                  border: form.value_sign === 'positive' ? '2px solid #16a34a' : '1px solid #e2e8f0',
                  background: form.value_sign === 'positive' ? '#f0fdf4' : '#fff',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <input
                    type="radio"
                    name="value_sign"
                    value="positive"
                    checked={form.value_sign === 'positive'}
                    onChange={handleChange}
                    style={{ width: 16, height: 16 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>➕ Positive</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>Displayed normally. Adds to composite.</div>
                  </div>
                </label>
                <label style={{
                  flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                  border: form.value_sign === 'negative' ? '2px solid #dc2626' : '1px solid #e2e8f0',
                  background: form.value_sign === 'negative' ? '#fef2f2' : '#fff',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <input
                    type="radio"
                    name="value_sign"
                    value="negative"
                    checked={form.value_sign === 'negative'}
                    onChange={handleChange}
                    style={{ width: 16, height: 16 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>➖ Negative</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>Shown as negative. Drags composite down.</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
                borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
                background: form.include_in_composite === false ? '#fef3c7' : '#fff',
              }}>
                <input
                  type="checkbox"
                  name="include_in_composite"
                  checked={form.include_in_composite !== false}
                  onChange={e => setForm(f => ({ ...f, include_in_composite: e.target.checked }))}
                  style={{ width: 16, height: 16, marginTop: 2 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                    Include in group overall composite score
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    When checked (default), this KPI contributes to the overall ranking at the top of the group's leaderboard.
                    Uncheck to make this KPI <b>display-only</b> — it still shows on the board but doesn't affect the overall rank.
                    Useful when two KPIs measure the same event (e.g., accounts lost + tows walked) and you don't want to double-count.
                  </div>
                </div>
              </label>
            </div>

            <div className="form-group">
              <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' }}>
                How multiple rounds combine
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <label style={{
                  flex: 1, padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                  border: form.aggregation_mode === 'sum' ? '2px solid #6d28d9' : '1px solid #e2e8f0',
                  background: form.aggregation_mode === 'sum' ? '#f5f3ff' : '#fff',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                }}>
                  <input
                    type="radio"
                    name="aggregation_mode"
                    value="sum"
                    checked={form.aggregation_mode !== 'latest'}
                    onChange={() => setForm(f => ({ ...f, aggregation_mode: 'sum' }))}
                    style={{ width: 16, height: 16, marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      ➕ Sum All Rounds
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      Each round's value gets added to the total. Data Entry inputs are per-round increments.
                      Use when each round is a fresh period (e.g., "this month's tows").
                    </div>
                  </div>
                </label>
                <label style={{
                  flex: 1, padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                  border: form.aggregation_mode === 'latest' ? '2px solid #6d28d9' : '1px solid #e2e8f0',
                  background: form.aggregation_mode === 'latest' ? '#f5f3ff' : '#fff',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                }}>
                  <input
                    type="radio"
                    name="aggregation_mode"
                    value="latest"
                    checked={form.aggregation_mode === 'latest'}
                    onChange={() => setForm(f => ({ ...f, aggregation_mode: 'latest' }))}
                    style={{ width: 16, height: 16, marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      🔄 Running Total (Replace)
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      Each round is a snapshot of the current value. Leaderboard shows the latest.
                      Data Entry <b>pre-fills with the current total</b> so you just update instead of re-entering.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' }}>
                Scoring Mode <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>— how this KPI calculates individual scores for composite ranking</span>
              </label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {SCORING_MODES.map(m => (
                  <label key={m.value} style={{
                    flex: 1, minWidth: 160, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                    border: form.scoring_mode === m.value ? '2px solid #6d28d9' : '1px solid #e2e8f0',
                    background: form.scoring_mode === m.value ? '#f5f3ff' : '#fff',
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                    <input
                      type="radio"
                      name="scoring_mode"
                      value={m.value}
                      checked={form.scoring_mode === m.value}
                      onChange={handleChange}
                      style={{ width: 16, height: 16, marginTop: 2 }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{m.label}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{m.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Goal / Target {form.scoring_mode === 'goal' ? '*' : '(optional)'}</label>
                <input name="goal" type="number" step="any" value={form.goal} onChange={handleChange} className="form-input" placeholder="e.g., 550" />
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                  {form.scoring_mode === 'goal' ? 'Required for goal-based scoring. Score = actual ÷ goal × 100.' : 'Optional — shown as a progress bar on the leaderboard'}
                </div>
              </div>
              <div className="form-group">
                <label>Data Source</label>
                <select name="input_method" value={form.input_method} onChange={handleChange} className="form-input">
                  {INPUT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Cumulative Label</label>
              <input name="cumulative_label" value={form.cumulative_label} onChange={handleChange} className="form-input" placeholder="e.g., Pay Period, MTD, Cumulative" />
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Label for the right-side totals panel on the leaderboard (e.g., "Pay Period", "MTD", "Season Total")</div>
            </div>

            {form.input_method === 'system' && (
              <div className="form-group">
                <label>System Query</label>
                <select name="system_query_type" value={form.system_query_type} onChange={handleChange} className="form-input">
                  {SYSTEM_QUERY_TYPES.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                </select>
              </div>
            )}

            <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', margin: '20px 0 12px', borderTop: '1px solid #edf2f7', paddingTop: '16px' }}>Period &amp; Rounds</h3>

            <div className="form-grid">
              <div className="form-group">
                <label>Period Type *</label>
                <select name="period_type" value={form.period_type} onChange={handleChange} className="form-input">
                  {PERIOD_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>How often this KPI resets (weekly, monthly, etc.)</div>
              </div>
              <div className="form-group">
                <label>Round Mode</label>
                <select name="round_mode" value={form.round_mode} onChange={handleChange} className="form-input">
                  <option value="manual">Manual — you add rounds when ready</option>
                  <option value="auto">Auto — system creates rounds each period</option>
                </select>
              </div>
            </div>

            {form.round_mode === 'auto' && (
              <div className="form-grid">
                {form.period_type === 'weekly' && (
                  <div className="form-group">
                    <label>Start Day</label>
                    <select name="start_day" value={form.start_day} onChange={handleChange} className="form-input">
                      {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Start Time</label>
                  <input type="time" name="start_time" value={form.start_time} onChange={handleChange} className="form-input" />
                </div>
                <div className="form-group">
                  <label>Timezone</label>
                  <select name="timezone" value={form.timezone} onChange={handleChange} className="form-input">
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                  </select>
                </div>
              </div>
            )}

            {form.round_mode === 'manual' && (
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#64748b' }}>
                You'll add rounds manually from the Data Entry page (e.g., "Week 1", "3/23 - 3/30").
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update KPI' : 'Create KPI')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {deleteConfirm && (
        <Modal title="Delete KPI" onClose={() => setDeleteConfirm(null)}>
          <p>Deactivate <strong>{deleteConfirm.name}</strong>?</p>
          <p style={{ fontSize: '13px', color: '#64748b' }}>Historical data will be preserved.</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Deactivate</button>
          </div>
        </Modal>
      )}

      {resetConfirm && (
        <Modal title="Reset KPI" onClose={() => setResetConfirm(null)}>
          <p>Reset <strong>{resetConfirm.name}</strong>?</p>
          <p style={{ fontSize: '13px', color: '#ef4444', fontWeight: 600 }}>This will delete ALL rounds and data for this KPI. This cannot be undone.</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setResetConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleReset(resetConfirm.id)}>Reset All Data</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
